import React from 'react';
import { API_URL } from '../config';
import type { WorkflowTestStreamEvent } from '@chatsdet/types';
import {
  applyWorkflowTestStreamEvent,
  createInitialWorkflowTestStreamMergeMeta,
  createInitialWorkflowTestStreamState,
  hydrateWorkflowTestStreamFromLiveState,
  type WorkflowExecutionLiveStateResponse,
  type WorkflowTestStreamMergeMeta,
  type WorkflowTestStreamState,
} from '../features/workflows/workflowTestStreamMerge';

export type ConnectionState = 'connected' | 'reconnecting' | 'failed';

const MAX_RECONNECT_ATTEMPTS = 5;

type WorkflowTestStreamReturn = WorkflowTestStreamState & {
  connectionState: ConnectionState;
  retry: () => void;
};

export function useWorkflowTestStream(suiteId: string | null): WorkflowTestStreamReturn {
  const [state, setState] = React.useState<WorkflowTestStreamState>(
    createInitialWorkflowTestStreamState
  );
  const [connectionState, setConnectionState] = React.useState<ConnectionState>('connected');
  const eventSourceRef = React.useRef<EventSource | null>(null);
  const reconnectTimerRef = React.useRef<number | null>(null);
  const reconnectAttemptRef = React.useRef(0);
  const mergeMetaRef = React.useRef<WorkflowTestStreamMergeMeta>(
    createInitialWorkflowTestStreamMergeMeta()
  );
  const suiteIdRef = React.useRef(suiteId);
  suiteIdRef.current = suiteId;

  // Retry callback ref to avoid stale closures
  const connectStreamRef = React.useRef<((since: string | null) => void) | undefined>(undefined);

  React.useEffect(() => {
    const closeStream = () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    if (!suiteId) {
      closeStream();
      setState(createInitialWorkflowTestStreamState());
      mergeMetaRef.current = createInitialWorkflowTestStreamMergeMeta();
      reconnectAttemptRef.current = 0;
      setConnectionState('connected');
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const connectStream = (since: string | null) => {
      if (cancelled) return;
      const url = new URL(`${API_URL}/workflows/test-runs/stream`);
      url.searchParams.set('suiteId', suiteId);
      if (since) {
        url.searchParams.set('since', since);
      }

      const eventSource = new EventSource(url.toString(), { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        reconnectAttemptRef.current = 0;
        setConnectionState('connected');
      };

      const handleEvent = (event: MessageEvent) => {
        try {
          const payload = JSON.parse(event.data) as WorkflowTestStreamEvent;
          setState((prev) => {
            const next = applyWorkflowTestStreamEvent(prev, mergeMetaRef.current, payload);
            mergeMetaRef.current = next.meta;
            return next.state;
          });
          reconnectAttemptRef.current = 0;
          setConnectionState('connected');
        } catch (error) {
          console.error('Failed to parse workflow test event', error);
        }
      };

      eventSource.addEventListener('workflow-test', handleEvent as EventListener);
      eventSource.addEventListener('workflow-test-suite', handleEvent as EventListener);
      eventSource.onerror = (err) => {
        console.error('Workflow test stream error', err);
        eventSource.close();
        if (cancelled) return;
        const nextAttempt = reconnectAttemptRef.current + 1;
        reconnectAttemptRef.current = nextAttempt;

        if (nextAttempt > MAX_RECONNECT_ATTEMPTS) {
          setConnectionState('failed');
          return;
        }

        setConnectionState('reconnecting');
        const backoffMs = Math.min(10000, 1000 * Math.pow(2, nextAttempt - 1));
        reconnectTimerRef.current = window.setTimeout(() => {
          connectStream(mergeMetaRef.current.streamSinceCursor);
        }, backoffMs);
      };
    };

    connectStreamRef.current = connectStream;

    const start = async () => {
      try {
        const response = await fetch(`${API_URL}/workflows/test-executions/${suiteId}/live-state`, {
          credentials: 'include',
          signal: controller.signal,
        });

        if (response.ok) {
          const liveState = (await response.json()) as WorkflowExecutionLiveStateResponse;
          if (!cancelled && liveState.ok) {
            const hydrated = hydrateWorkflowTestStreamFromLiveState(liveState);
            mergeMetaRef.current = hydrated.meta;
            setState(hydrated.state);
          }
        }
      } catch (error) {
        if ((error as DOMException)?.name !== 'AbortError') {
          console.error('Failed to hydrate workflow test stream state', error);
        }
      }

      connectStream(mergeMetaRef.current.streamSinceCursor);
    };

    void start();

    return () => {
      cancelled = true;
      controller.abort();
      closeStream();
    };
  }, [suiteId]);

  const retry = React.useCallback(() => {
    if (!suiteIdRef.current) return;
    reconnectAttemptRef.current = 0;
    setConnectionState('reconnecting');
    // Close existing stream before reconnecting
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    connectStreamRef.current?.(mergeMetaRef.current.streamSinceCursor);
  }, []);

  return {
    ...state,
    connectionState,
    retry,
  };
}

export type { WorkflowTestStreamState };
